/**
 * Current profile hooks — useCurrentProfile, useUpdateProfile.
 * See ARCHITECTURE.md §4.1
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { toast } from '@/lib/toast'
import { env } from '@/env'
import { getDevSession } from '@/lib/devSession'
import { DEV_PROFILES } from '@/lib/mockData'
import type { Tables } from '@/lib/db.types'

type Profile = Tables<'profiles'>

export const profileKeys = {
  all: ['profiles'] as const,
  me: () => [...profileKeys.all, 'me'] as const,
}

/**
 * Fetch the currently signed-in user's profile.
 */
export function useCurrentProfile() {
  return useQuery({
    queryKey: profileKeys.me(),
    queryFn: async (): Promise<Profile> => {
      // Dev escape hatch — return a rich synthetic profile when no backend is configured.
      if (env.IS_PLACEHOLDER_BACKEND) {
        const dev = getDevSession()
        if (!dev) throw new Error('Ej inloggad')
        const base = DEV_PROFILES[dev.role]
        // Mix the devSession's chosen full_name/email (if set) into the rich base.
        return {
          ...base,
          id: dev.userId,
          email: dev.email,
          full_name: dev.fullName,
        }
      }

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Ej inloggad')

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error) throw error
      return data
    },
  })
}

export interface UpdateProfileInput {
  full_name?: string
  phone?: string | null
  avatar_url?: string | null
  preferred_language?: string
}

/**
 * Update the current user's profile. Invalidates cache immediately.
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: UpdateProfileInput) => {
      if (env.IS_PLACEHOLDER_BACKEND) {
        const dev = getDevSession()
        if (!dev) throw new Error('Ej inloggad')
        const base = DEV_PROFILES[dev.role]
        return { ...base, ...input, id: dev.userId, email: dev.email } as Profile
      }

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Ej inloggad')

      const { data, error } = await supabase
        .from('profiles')
        .update(input)
        .eq('id', user.id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.me() })
      toast.success('Profil uppdaterad')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Kunde inte uppdatera profil')
    },
  })
}
